// Visits for the whole team (D-186): who handles a booked visit, where visits happen and the call to confirm one. The
// company sets them in Settings; the database checks them again (organization_config_problem) and applies the rule when
// a visit is booked without naming who takes it. The WhatsApp message sent on booking one is one of the company's
// messages marked for visits (D-187), or the standard one below.
import { fillMessage } from "./crm";
import type { Json } from "./database.types";

export type VisitHandler = "booker" | "one" | "in_turn";
export type VisitReminder = "off" | "hours_before" | "evening_before";

/** A company's visit settings as Settings edits them. */
export interface VisitSettings {
  handler: VisitHandler;
  /** For "one": the person who handles every visit. */
  handler_member: string | null;
  /** For "in_turn": the people who take visits in turn. */
  handler_members: string[];
  /** Where visits happen, when they come to the company. */
  place: string;
  /** A Google Maps (or other) link to the place. */
  map_link: string;
  /** The call to confirm a visit; null: never chosen, so the platform's switch decides (D-139). */
  reminder: VisitReminder | null;
  reminder_hours: number;
}

/** What the signed-in member's status says about visits (my_status), for booking one. */
export interface VisitPublic {
  handler: VisitHandler;
  handler_member: { id: string; name: string } | null;
  place: string | null;
  map_link: string | null;
  reminder: VisitReminder | null;
  reminder_hours: number;
}

export const VISIT_DEFAULTS: VisitSettings = {
  handler: "booker",
  handler_member: null,
  handler_members: [],
  place: "",
  map_link: "",
  reminder: null,
  reminder_hours: 2,
};

/** The message sent when the company has marked none for visits. A line whose detail is missing is left out. */
export const DEFAULT_VISIT_MESSAGE = "Hello {name}, your visit to {company} is booked for {day} at {time}.\n" +
  "Address: {place}\n{map}\n{handler} will meet you.\n– {caller}";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The visit settings inside a company's settings, with defaults for what is missing or unreadable. */
export function parseVisitSettings(json: Json | null | undefined): VisitSettings {
  const v = json && typeof json === "object" && !Array.isArray(json) ? json as Record<string, unknown> : {};
  const text = (x: unknown) => (typeof x === "string" ? x : "");
  const handler = (["booker", "one", "in_turn"] as const).find((h) => h === v.handler) ?? "booker";
  const reminder = (["off", "hours_before", "evening_before"] as const).find((r) => r === v.reminder) ?? null;
  return {
    handler,
    handler_member: typeof v.handler_member === "string" && UUID.test(v.handler_member) ? v.handler_member : null,
    handler_members: Array.isArray(v.handler_members)
      ? v.handler_members.filter((m): m is string => typeof m === "string" && UUID.test(m))
      : [],
    place: text(v.place),
    map_link: text(v.map_link),
    reminder,
    reminder_hours: Number.isInteger(v.reminder_hours) && (v.reminder_hours as number) >= 1 &&
        (v.reminder_hours as number) <= 6
      ? v.reminder_hours as number
      : 2,
  };
}

/** The visit settings to save, or null when nothing differs from the defaults: a company that never set anything keeps
 * no visit settings, so its visits work exactly as before. */
export function visitSettingsJson(s: VisitSettings): Json | null {
  const out: Record<string, Json> = {};
  if (s.handler !== "booker") out.handler = s.handler;
  if (s.handler === "one" && s.handler_member) out.handler_member = s.handler_member;
  if (s.handler === "in_turn") out.handler_members = s.handler_members;
  if (s.place.trim()) out.place = s.place.trim();
  if (s.map_link.trim()) out.map_link = s.map_link.trim();
  if (s.reminder) out.reminder = s.reminder;
  if (s.reminder === "hours_before") out.reminder_hours = s.reminder_hours;
  return Object.keys(out).length ? out : null;
}

/** What is wrong with the visit settings, in the database's words, or null. */
export function visitSettingsProblem(s: VisitSettings): string | null {
  if (s.handler === "one" && !s.handler_member) return "Choose who handles visits.";
  if (s.handler === "in_turn" && s.handler_members.length === 0) return "Choose who takes visits in turn.";
  if (s.handler_members.length > 20) return "Choose up to 20 people to take visits in turn.";
  if (s.place.trim().length > 300) return "Keep where visits happen under 300 characters.";
  if (s.map_link.trim() && !/^https:\/\/\S+$/.test(s.map_link.trim())) {
    return "The map link is a web address starting with https://.";
  }
  if (s.map_link.trim().length > 500) return "The map link is a web address starting with https://.";
  if (!Number.isInteger(s.reminder_hours) || s.reminder_hours < 1 || s.reminder_hours > 6) {
    return "The call to confirm a visit falls due 1 to 6 hours before it.";
  }
  return null;
}

/** A visit message with its details filled in (fillMessage); no message: the standard one. */
export function fillVisitMessage(
  template: string | null | undefined,
  values: {
    name?: string | null;
    day: string;
    time: string;
    place?: string | null;
    map?: string | null;
    handler?: string | null;
    caller?: string | null;
    company?: string | null;
  },
): string {
  const { name, caller, ...details } = values;
  return fillMessage((template ?? "").trim() || DEFAULT_VISIT_MESSAGE, name, caller, details);
}

/** A visit's day and time as the message says them: "Friday 24 Oct", "4:00 pm", in the company's time zone. */
export function visitWhen(iso: string, tz: string): { day: string; time: string } {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat("en-IN", { weekday: "long", day: "numeric", month: "short", timeZone: tz })
    .format(d).replace(",", "");
  const time = new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz })
    .format(d).replace(/\s?(am|pm)$/i, (m) => ` ${m.trim().toLowerCase()}`);
  return { day, time };
}
